import React, { memo, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SUBCATEGORIES from '../data/subcategories';
import ScrollHintRow from './common/ScrollHintRow';
import './ShopCategoryGrid.css';

const COVER_IMAGES = {
  // 1:1 mapping — no duplicates between unrelated categories
  electronics_accessories: '/assets/categories/3d_electronics_accessories.png',
  vehicles_motorbikes: '/assets/categories/3d_vehicles_motorcycles.png',
  vehicle_spare_parts: '/assets/categories/3d_vehicles_spare_parts.png',
  real_estate: '/assets/categories/3d_real_estate.png',
  furniture: '/assets/categories/3d_furniture.png',
  clothes_shoes: '/assets/categories/3d_clothes_shoes.png',
  mobile_accessories: '/assets/categories/3d_mobile_accessories.png',
  laptops_accessories: '/assets/categories/3d_laptop_accessories.png',
  health_fitness: '/assets/categories/3d_health_fitness.png',
  sports_equipment: '/assets/categories/3d_sports_equipment.png',
  jewellery: '/assets/categories/3d_jewellery.png',
  beauty_personal_care: '/assets/categories/3d_womens_beauty.png',
  farm_products: '/assets/categories/3d_agriculture.png',
  agriculture: '/assets/categories/3d_agriculture.png',
  food_beverage: '/assets/categories/3d_food_beverage.png',
  fabric_raw_materials: '/assets/categories/3d_fabric_raw_materials.png',
  pets_cattle: '/assets/categories/3d_pets_cattle.png',
  industrial_machinery: '/assets/categories/3d_industrial_machinery.png',
  gifts_crafts: '/assets/categories/3d_gifts_crafts.png',
  it_equipment_software: '/assets/categories/3d_it_equipment.png',
  auto_parts: '/assets/categories/3d_vehicles_spare_parts.png',
  womens_fashion: '/assets/categories/3d_womens_beauty.png',
  // Categories below use emoji fallback (no dedicated image to avoid wrong duplicates)
};

// Emoji icons for categories without dedicated 3D images
const CATEGORY_EMOJI = {
  jobs: '💼', services: '🔧', energy: '⚡', home_supplies: '🧹',
  garden_decorations: '🌱', home_appliances: '🏠', metals_alloys: '🔩',
  medical_devices: '🩺', commodities: '📦', games_toys: '🎮',
  books_notes: '📚', office_school_supplies: '✏️', security_safety: '🛡️',
  equipment_tools: '🔨', apparel_accessories: '👜', womens_beauty: '💄',
};

const FALLBACK_IMG = null;

const ShopCategoryGrid = memo(function ShopCategoryGrid({
  categories,
  selectedCategory,
  selectedSubcategory,
  onSelectCategory,
  onSelectSubcategory,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const [expandedCat, setExpandedCat] = useState(null);

  const filtered = categories.filter(
    c => c.key !== 'all' && c.key !== 'recommended' && c.key !== 'student_hub' && !c.special
  );

  // Get subcategories for the expanded category
  const subcats = useMemo(() => {
    if (!expandedCat) return [];
    return SUBCATEGORIES[expandedCat] || [];
  }, [expandedCat]);

  const handleCategoryClick = (catKey) => {
    const hasSubs = SUBCATEGORIES[catKey] && SUBCATEGORIES[catKey].length > 0;

    if (hasSubs) {
      // Toggle subcategory panel
      if (expandedCat === catKey) {
        setExpandedCat(null);
      } else {
        setExpandedCat(catKey);
      }
      // Select the parent category to highlight it
      onSelectCategory(catKey);
    } else {
      // No subcategories — filter directly
      setExpandedCat(null);
      onSelectCategory(catKey);
    }
  };

  const handleSubcategoryClick = (subKey) => {
    if (onSelectSubcategory) {
      onSelectSubcategory(subKey);
    }
  };

  if (!filtered.length) return null;

  return (
    <section className="shopCatSection" data-testid="shop-category-grid">
      <div className="shopCatHeader">
        <h3 className="shopCatHeaderTitle">
          {t('scg.shopByCategory')}
        </h3>
      </div>

      <ScrollHintRow
        storageKey="wassla-nudge-shop-cat-rail"
        testid="shop-cat-rail-hint"
      >
        <div className="catRail" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="catRail__grid">
            {filtered.map((c) => {
              const label = isRTL ? (c.name_ar || c.name_en) : (c.name_en || c.name_ar);
              const imgSrc = COVER_IMAGES[c.key] || null;
              const emoji = !imgSrc ? (CATEGORY_EMOJI[c.key] || '📦') : null;
              const isActive = c.key === selectedCategory || c.key === expandedCat;

              return (
                <button
                  key={c.key}
                  type="button"
                  className={`catCard ${isActive ? 'catCardActive' : ''}`}
                  onClick={() => handleCategoryClick(c.key)}
                  data-testid={`shop-cat-tile-${c.key}`}
                >
                  <div className="catCard__text">
                    <div className="catCard__title">{label}</div>
                  </div>
                  <div className="catCard__media">
                    {imgSrc ? (
                      <img src={imgSrc} alt={label} loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.nextSibling.style.display='flex'; }} />
                    ) : null}
                    <div className="catCard__emoji" style={imgSrc ? {display:'none'} : {}}>
                      {emoji}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </ScrollHintRow>

      {/* Subcategory chips */}
      {expandedCat && subcats.length > 0 && (
        <div className="subcatSection" data-testid="subcategory-chips">
          <div className="subcatLabel">
            {t('scg.browseSubcategories')}
          </div>
          <div className="subcatChipsOuter" dir={isRTL ? 'rtl' : 'ltr'}>
            <ScrollHintRow
              storageKey={`wassla-nudge-shop-subcat-${expandedCat}`}
              testid="shop-subcat-chips-hint"
            >
              <div className="subcatChips">
                {/* "All" chip */}
                <button
                  type="button"
                  className={`subcatChip ${!selectedSubcategory ? 'subcatChipActive' : ''}`}
                  onClick={() => onSelectSubcategory(null)}
                >
                  {t('slp.all')}
                </button>
                {subcats.map((s, i) => {
                  const subLabel = isRTL ? (s.name_ar || s.name_en) : (s.name_en || s.name_ar);
                  const isSubActive = s.key === selectedSubcategory;
                  return (
                    <button
                      key={`${s.key}-${i}`}
                      type="button"
                      className={`subcatChip ${isSubActive ? 'subcatChipActive' : ''}`}
                      onClick={() => handleSubcategoryClick(s.key)}
                    >
                      {subLabel}
                    </button>
                  );
                })}
              </div>
            </ScrollHintRow>
          </div>
        </div>
      )}
    </section>
  );
});

export default ShopCategoryGrid;
